import { useState, useEffect, useCallback } from "react";
import { apiFetch, ApiError } from "@/lib/api-client";
import type { FolioAuthorization } from "../types";

interface UseFolioAuthorizationsResult {
  folios: FolioAuthorization[];
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
  uploadCaf: (cafXml: string) => Promise<FolioAuthorization>;
  isUploading: boolean;
  deactivateFolio: (id: string) => Promise<void>;
}

/**
 * Hook to manage folio authorizations (CAFs).
 */
export function useFolioAuthorizations(): UseFolioAuthorizationsResult {
  const [folios, setFolios] = useState<FolioAuthorization[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [fetchKey, setFetchKey] = useState(0);

  useEffect(() => {
    const fetchFolios = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const data = await apiFetch.get<{ folios: FolioAuthorization[] }>(
          "/api/tax-authority/folios",
        );
        setFolios(data.folios);
      } catch (err) {
        if (err instanceof ApiError) {
          setError(err);
        } else {
          setError(err instanceof Error ? err : new Error("Unknown error"));
        }
        setFolios([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchFolios();
  }, [fetchKey]);

  const refetch = useCallback(() => setFetchKey((k) => k + 1), []);

  const uploadCaf = useCallback(
    async (cafXml: string): Promise<FolioAuthorization> => {
      setIsUploading(true);
      try {
        const result = await apiFetch.post<{
          folio: {
            id: string;
            documentType: string;
            documentTypeCode: string;
            folioStart: number;
            folioEnd: number;
            folioCurrent: number;
            authorizedAt: string;
          };
        }>("/api/tax-authority/folios", { cafXml });

        const newFolio: FolioAuthorization = {
          ...result.folio,
          foliosRemaining:
            result.folio.folioEnd - result.folio.folioCurrent + 1,
          expiresAt: null,
          isActive: true,
        };

        // Refetch to get updated list
        refetch();
        return newFolio;
      } finally {
        setIsUploading(false);
      }
    },
    [refetch],
  );

  const deactivateFolio = useCallback(
    async (id: string): Promise<void> => {
      await apiFetch.delete(`/api/tax-authority/folios/${id}`);
      refetch();
    },
    [refetch],
  );

  return {
    folios,
    isLoading,
    error,
    refetch,
    uploadCaf,
    isUploading,
    deactivateFolio,
  };
}
